import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { processSession } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { safeNextPath } from "../lib/safeNextPath";

/**
 * Mounted when the URL fragment includes `session_id=` (detected
 * synchronously by AppRouter during render — never via useEffect).
 *
 * Exchanges the session_id with our backend, sets the cookie, populates
 * the global auth state, then redirects to the `next` query param.
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser } = useAuth();
    // Use a ref so StrictMode's double-invoke doesn't double-POST.
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;
        async function run() {
            try {
                const hash = window.location.hash || "";
                const match = hash.match(/session_id=([^&]+)/);
                const session_id = match ? decodeURIComponent(match[1]) : null;
                if (!session_id) {
                    throw new Error("session_id missing from URL");
                }
                const me = await processSession(session_id);
                setUser(me);
                // Issue #35 — validate `next` before navigating to it, so
                // an attacker-controlled `?next=` param on the login URL
                // can't send the post-login redirect off-origin.
                const next = safeNextPath(
                    new URLSearchParams(location.search).get("next"),
                );
                // Strip the session_id from the URL on the way out
                navigate(next, { replace: true, state: { user: me } });
            } catch (e) {
                setError(e.message || "Sign-in failed");
            }
        }
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            data-testid="auth-callback"
            className="min-h-screen flex items-center justify-center bg-paper px-6"
        >
            <div className="max-w-md text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-accent mb-3">
                    Signing you in
                </div>
                {error ? (
                    <>
                        <p className="text-ink font-serif text-lg leading-relaxed mb-4">
                            We couldn&rsquo;t complete the sign-in.
                        </p>
                        <p className="text-ink-soft text-sm font-mono">{error}</p>
                        <button
                            data-testid="auth-callback-retry"
                            className="mt-6 px-4 py-2 bg-ink text-paper text-[12px] tracking-wide hover:bg-amber-accent transition-colors"
                            onClick={() => navigate("/", { replace: true })}
                        >
                            Return home
                        </button>
                    </>
                ) : (
                    <p className="text-ink-soft font-serif text-lg leading-relaxed">
                        Verifying your session and landing you back where you left off&hellip;
                    </p>
                )}
            </div>
        </div>
    );
}
